"use client";
/**
 * Interactive UI primitives. Forms use React 19 `useActionState` with server actions returning ActionState.
 */
import { createContext, startTransition, useActionState, useContext, useEffect, useRef, useState, type ComponentProps, type FormEvent, type ReactNode } from "react";
import { useFormStatus } from "react-dom";
import { useT } from "@/i18n/client";
import { Alert, Button, buttonClass, cx } from "./index";

export type ActionState = { ok?: boolean; message?: string; error?: string; fieldErrors?: Record<string, string>; data?: Record<string, unknown> };
export type FormAction = (state: ActionState, formData: FormData) => Promise<ActionState>;

const PendingContext = createContext<boolean | null>(null);

/** Submit button that shows a pending state (inside ActionForm or any native form action). */
export function SubmitButton({ children, pendingText, variant = "primary", size = "md", className, ...p }: ComponentProps<"button"> & { pendingText?: string; variant?: Parameters<typeof buttonClass>[0]; size?: Parameters<typeof buttonClass>[1] }) {
  const t = useT();
  const ctx = useContext(PendingContext);
  const status = useFormStatus();
  const pending = ctx ?? status.pending;
  return (
    <button {...p} type="submit" disabled={pending || p.disabled} aria-busy={pending} className={buttonClass(variant, size, className)}>
      {pending && <Spinner />}
      {pending ? pendingText ?? t("common.saving") : children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx("size-4 animate-spin", className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Form wrapper around a server action. Shows success/error messages and passes field errors to children via
 * a render prop. Submits through onSubmit + startTransition (not `<form action>`), because React 19 resets
 * uncontrolled fields after a form action completes — even on validation errors — which lost user input.
 *   <ActionForm action={saveThing}>{(s) => <Field error={s.fieldErrors?.name}>…</Field>}</ActionForm>
 */
export function ActionForm({
  action,
  children,
  className,
  successMessage,
  resetOnSuccess,
  onSuccess,
  id,
}: {
  action: FormAction;
  children: ReactNode | ((state: ActionState) => ReactNode);
  className?: string;
  successMessage?: string;
  resetOnSuccess?: boolean;
  onSuccess?: (state: ActionState) => void;
  id?: string;
}) {
  const [state, formAction, pending] = useActionState(action, {});
  const ref = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (state.ok) {
      if (resetOnSuccess) ref.current?.reset();
      onSuccess?.(state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLElement | null;
    const fd = new FormData(e.currentTarget, submitter);
    startTransition(() => formAction(fd));
  }
  return (
    <form ref={ref} onSubmit={onSubmit} className={className} id={id} aria-busy={pending}>
      {state.error && (
        <Alert tone="error" className="mb-4">
          {state.error}
        </Alert>
      )}
      {state.ok && (state.message || successMessage) && (
        <Alert tone="success" className="mb-4">
          {state.message ?? successMessage}
        </Alert>
      )}
      <PendingContext.Provider value={pending}>{typeof children === "function" ? children(state) : children}</PendingContext.Provider>
    </form>
  );
}

/** A one-button form for an action (e.g. "Publish", "Delete") with optional confirmation dialog. */
export function ActionButton({
  action,
  children,
  confirm,
  variant = "secondary",
  size = "md",
  hidden,
  className,
  pendingText,
}: {
  action: FormAction;
  children: ReactNode;
  confirm?: { title: string; body?: ReactNode; confirmLabel?: string; danger?: boolean; requireReason?: boolean; reasonLabel?: string };
  variant?: Parameters<typeof buttonClass>[0];
  size?: Parameters<typeof buttonClass>[1];
  hidden?: Record<string, string>;
  className?: string;
  pendingText?: string;
}) {
  const t = useT();
  const [state, formAction] = useActionState(action, {});
  const dialog = useRef<HTMLDialogElement>(null);
  const inputs = Object.entries(hidden ?? {}).map(([k, v]) => <input key={k} type="hidden" name={k} value={v} />);
  if (!confirm) {
    return (
      <form action={formAction} className={cx("inline", className)}>
        {inputs}
        <SubmitButton variant={variant} size={size} pendingText={pendingText ?? t("ui.working")}>
          {children}
        </SubmitButton>
        {state.error && <span className="ms-2 text-sm text-red-600" role="alert">{state.error}</span>}
        {state.ok && state.message && <span className="ms-2 text-sm text-emerald-700" role="status">{state.message}</span>}
      </form>
    );
  }
  return (
    <>
      <button type="button" className={buttonClass(variant, size, className)} onClick={() => dialog.current?.showModal()}>
        {children}
      </button>
      {state.error && <span className="ms-2 text-sm text-red-600" role="alert">{state.error}</span>}
      {state.ok && state.message && <span className="ms-2 text-sm text-emerald-700" role="status">{state.message}</span>}
      <dialog ref={dialog} className="m-auto w-[min(92vw,28rem)] rounded-xl p-0 shadow-xl" aria-labelledby="confirm-title">
        <form
          action={async (fd) => {
            await formAction(fd);
            dialog.current?.close();
          }}
          className="p-6"
        >
          {inputs}
          <h2 id="confirm-title" className="text-lg font-semibold text-zinc-900">
            {confirm.title}
          </h2>
          {confirm.body && <div className="mt-2 text-sm text-zinc-600">{confirm.body}</div>}
          {confirm.requireReason && (
            <label className="mt-4 block text-sm">
              <span className="font-medium text-zinc-800">{confirm.reasonLabel ?? t("ui.reason")}</span>
              <textarea name="reason" required className="mt-1 block w-full rounded-lg border-0 px-3 py-2 text-sm ring-1 ring-inset ring-zinc-300 focus:ring-2 focus:ring-brand-600" />
            </label>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => dialog.current?.close()}>
              {t("common.cancel")}
            </Button>
            <SubmitButton variant={confirm.danger ? "danger" : "primary"} pendingText={t("ui.working")}>
              {confirm.confirmLabel ?? t("common.confirm")}
            </SubmitButton>
          </div>
        </form>
      </dialog>
    </>
  );
}

/** Generic modal dialog with a trigger button. */
export function Modal({ trigger, title, children, triggerVariant = "secondary", triggerSize = "md", wide }: { trigger: ReactNode; title: string; children: (close: () => void) => ReactNode; triggerVariant?: Parameters<typeof buttonClass>[0]; triggerSize?: Parameters<typeof buttonClass>[1]; wide?: boolean }) {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const close = () => {
    ref.current?.close();
    setOpen(false);
  };
  return (
    <>
      <button
        type="button"
        className={buttonClass(triggerVariant, triggerSize)}
        onClick={() => {
          setOpen(true);
          ref.current?.showModal();
        }}
      >
        {trigger}
      </button>
      <dialog ref={ref} onClose={() => setOpen(false)} className={cx("m-auto rounded-xl p-0 shadow-xl", wide ? "w-[min(96vw,48rem)]" : "w-[min(92vw,32rem)]")} aria-label={title}>
        <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={close} className="rounded p-1 text-zinc-500 hover:bg-zinc-100" aria-label={t("common.close")}>
            ✕
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-6">{open && children(close)}</div>
      </dialog>
    </>
  );
}

/** Copy-to-clipboard button (for webhook URLs, DNS records…). */
export function CopyButton({ value, label }: { value: string; label?: string }) {
  const t = useT();
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={buttonClass("secondary", "sm")}
      onClick={async () => {
        await navigator.clipboard.writeText(value).catch(() => undefined);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? t("common.copied") : label ?? t("common.copy")}
    </button>
  );
}

/** Image/file uploader posting to /api/stores/:storeId/media. Calls onUploaded with the media record. */
export function Uploader({
  storeId,
  visibility = "public",
  accept = "image/png,image/jpeg,image/webp,image/gif,image/avif",
  multiple,
  onUploaded,
  label,
}: {
  storeId: string;
  visibility?: "public" | "private";
  accept?: string;
  multiple?: boolean;
  onUploaded: (m: { id: string; filename: string; width: number | null; height: number | null; alt: string; mimeType: string; sizeBytes: number }) => void;
  label?: string;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("visibility", visibility);
      const res = await fetch(`/api/stores/${storeId}/media`, { method: "POST", body: fd });
      const body = await res.json().catch(() => ({ error: t("ui.uploadFailed") }));
      if (!res.ok) {
        setError(body.error ?? t("ui.uploadFailed"));
        break;
      }
      onUploaded(body.media);
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  }
  return (
    <div>
      <label
        className={cx("flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-zinc-300 px-4 py-6 text-center text-sm text-zinc-600 hover:border-brand-400 hover:bg-brand-50/40", busy && "opacity-60")}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          upload(e.dataTransfer.files);
        }}
      >
        <input ref={inputRef} type="file" className="sr-only" accept={accept} multiple={multiple} onChange={(e) => upload(e.target.files)} disabled={busy} />
        <span className="font-medium text-brand-700">{busy ? t("common.uploading") : label ?? t("common.upload")}</span>
        <span className="mt-1 text-xs text-zinc-500">{visibility === "private" ? t("ui.uploadHintPrivate") : t("ui.uploadHintPublic")}</span>
      </label>
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
}

/** Decimal money input that keeps the raw string and shows the currency. */
export function MoneyInput({ name, defaultValue, currency, id, required, placeholder }: { name: string; defaultValue?: string; currency: string; id?: string; required?: boolean; placeholder?: string }) {
  return (
    <div className="relative">
      <input
        id={id ?? name}
        name={name}
        defaultValue={defaultValue}
        inputMode="decimal"
        pattern="^\d+([.,]\d{0,3})?$"
        required={required}
        placeholder={placeholder ?? "0.00"}
        className="block h-10 w-full rounded-lg border-0 bg-white py-2 ps-3 pe-14 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:ring-2 focus:ring-inset focus:ring-brand-600"
      />
      <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-xs font-medium text-zinc-500">{currency}</span>
    </div>
  );
}
